// services/upgradeCheckout.js
//
// Paying for an upgrade - an invitation tier, photo uploads, album storage -
// through Razorpay, in the buyer's currency.
//
//   1. upgrade-create-order prices the purchase on the server (from the
//      database, never from here) and opens a Razorpay order.
//   2. Razorpay's checkout takes the card / UPI payment.
//   3. upgrade-verify-payment checks the signature with Razorpay, then issues
//      the invoice and applies the upgrade.
//
// Both are Supabase Edge Functions (invits-app-new/supabase/functions). If
// step 3 never happens (the tab closed), razorpay-webhook finishes the
// purchase instead. The price rules are shared/upgradePricing.js, the same
// module the server charges with, so what is shown is what is charged.

import supabase from './supabase';
import { currencyForCountry } from '../shared/upgradePricing';

/** The seller's state: buyers there pay CGST + SGST, the rest of India IGST. */
export const SELLER_STATE = 'Chhattisgarh';

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function post(fn, body) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Please sign in again to continue.');
  const { data, error } = await supabase.functions.invoke(fn, { body });
  if (error) {
    // A non-2xx: the function's own message is in the response body.
    const detail = await error.context?.json?.().catch(() => null);
    throw new Error(detail?.error || error.message || 'Something went wrong');
  }
  return data;
}

export const currencyFor = currencyForCountry;

// ── Razorpay checkout ───────────────────────────────────────────────────────

let scriptPromise = null;

export function loadRazorpay() {
  if (window.Razorpay) return Promise.resolve(window.Razorpay);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://checkout.razorpay.com/v1/checkout.js';
      s.async = true;
      s.onload = () => resolve(window.Razorpay);
      s.onerror = () => { scriptPromise = null; reject(new Error('Could not load the payment window. Check your connection and try again.')); };
      document.body.appendChild(s);
    });
  }
  return scriptPromise;
}

/** Resolves with Razorpay's payment response; rejects if closed or failed. */
function openCheckout(Razorpay, order, customer) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const rzp = new Razorpay({
      key: order.keyId,
      order_id: order.orderId,
      amount: order.amount,
      currency: order.currency,
      name: 'Invits',
      description: order.description,
      prefill: {
        name: customer.name,
        email: customer.email,
        contact: customer.phone || undefined,
      },
      notes: { kind: order.kind },
      theme: { color: '#7C4DFF' },
      handler: (response) => { settled = true; resolve(response); },
      modal: {
        ondismiss: () => {
          if (!settled) reject(Object.assign(new Error('Payment was cancelled.'), { cancelled: true }));
        },
      },
    });
    rzp.on('payment.failed', (resp) => {
      settled = true;
      reject(new Error(resp?.error?.description || 'The payment did not go through.'));
    });
    rzp.open();
  });
}

/**
 * The whole purchase.
 *
 * @param {object} p
 * @param {'event_tier'|'photo_upload'|'album'} p.kind
 * @param {object} p.fields     kind-specific: { eventId, tierId } | { eventId, financialYear,
 *   purchaseType, tierId } | { albumId, durationMonths }
 * @param {{name, email, phone, state, country}} p.customer
 * @param {(stage: 'paying'|'verifying') => void} [p.onStage]
 * @returns {Promise<{ invoiceNumber, invoiceId, currency, amount, quote }>}
 */
export async function payForUpgrade({ kind, fields, customer, onStage }) {
  const [Razorpay, order] = await Promise.all([
    loadRazorpay(),
    post('upgrade-create-order', { kind, ...fields, customer }),
  ]);
  if (!order?.success) throw new Error(order?.error || 'Could not start the payment.');

  onStage?.('paying');
  const response = await openCheckout(Razorpay, order, customer);

  onStage?.('verifying');
  let result = await post('upgrade-verify-payment', response);
  // The webhook got there first and is still finishing (or verify could not
  // finish and the webhook will): give it a moment.
  for (let i = 0; (result?.paymentStatus === 'processing' || result?.pending) && i < 5; i += 1) {
    await sleep(2000);
    result = await post('upgrade-verify-payment', response);
  }
  // Paid, and the webhook is still applying it: that is a success the page can
  // show now. The upgrade and invoice land within moments.
  if (result?.pending || result?.paymentStatus === 'processing') {
    result = { ...result, success: true, paymentStatus: 'processing' };
  }
  if (!result?.success) throw new Error(result?.error || 'We could not confirm the payment.');

  return {
    ...result,
    currency: order.currency,
    amount: order.amount / 100,
    quote: order.quote,
  };
}
